import { nextMonday, nextTuesday, nextWednesday } from 'date-fns'
import uniqueId from 'lodash-es/uniqueId'

import { createDate, toUkTimezoneDate } from '../../../utils/date'
import {
  answerAppointmentFollowUp,
  AppointmentFollowUpAction,
  createAppointment,
} from '../../Appointment'

import { AppointmentFollowUpsFixture } from './AppointmentFollowUps.constants'

const APPOINTMENT_FOLLOW_UPS_REQUEST_ID_BASE = 990000

const APPOINTMENT_FOLLOW_UPS_START_HOUR = 10

const APPOINTMENT_FOLLOW_UPS_END_HOUR = 11

const APPOINTMENT_FOLLOW_UPS_MISSING_ID = 'apt-missing'

function getAppointmentFollowUpsTime({ getDay, hour, timezone }) {
  const day = getDay(toUkTimezoneDate(new Date(), timezone))

  return createDate(
    timezone,
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    hour
  ).toISOString()
}

function createAppointmentFollowUpsAppointment({
  getDay = nextMonday,
  requestId,
  restoreJobEnabled = false,
  timezone,
}) {
  return createAppointment({
    data: {
      end_at: getAppointmentFollowUpsTime({
        getDay,
        hour: APPOINTMENT_FOLLOW_UPS_END_HOUR,
        timezone,
      }),
      is_all_day: false,
      request: requestId,
      start_at: getAppointmentFollowUpsTime({
        getDay,
        hour: APPOINTMENT_FOLLOW_UPS_START_HOUR,
        timezone,
      }),
      title: 'Site visit',
    },
    mockContext: {
      anchors: {
        request: {
          can_edit_due_date: true,
          due_date: null,
          number: `SR-${requestId}`,
          title: 'Heating, Bergstraße 16',
        },
      },
      restoreJobEnabled,
      timezone,
    },
  }).then(({ data }) => data)
}

function withAppointmentFollowUpsAdvisory({ advisory, appointment }) {
  return {
    ...appointment,
    follow_up_prompt: {
      ...appointment.follow_up_prompt,
      day_load_advisory: advisory,
    },
  }
}

// A colleague's appointment on the same request moves its due date before the move
function createAppointmentFollowUpsChangedMeanwhile(options) {
  return createAppointmentFollowUpsAppointment(options).then(appointment =>
    createAppointmentFollowUpsAppointment({ ...options, getDay: nextTuesday })
      .then(colleagueAppointment =>
        answerAppointmentFollowUp({
          data: {
            action: AppointmentFollowUpAction.apply,
            expected_current_due_date:
              colleagueAppointment.follow_up_prompt.current_due_date,
          },
          id: colleagueAppointment.id,
        })
      )
      .then(() => appointment)
  )
}

export function getAppointmentFollowUpsFixture({ fixture, timezone }) {
  const options = {
    requestId: APPOINTMENT_FOLLOW_UPS_REQUEST_ID_BASE + Number(uniqueId()),
    timezone,
  }

  switch (fixture) {
    case AppointmentFollowUpsFixture.fullDay:
      return createAppointmentFollowUpsAppointment({
        ...options,
        getDay: nextWednesday,
      })
    case AppointmentFollowUpsFixture.limitOff:
      return createAppointmentFollowUpsAppointment(options).then(appointment =>
        withAppointmentFollowUpsAdvisory({ advisory: null, appointment })
      )
    case AppointmentFollowUpsFixture.loadUnavailable:
      return createAppointmentFollowUpsAppointment(options).then(appointment =>
        withAppointmentFollowUpsAdvisory({
          advisory: { unavailable: true },
          appointment,
        })
      )
    case AppointmentFollowUpsFixture.restore:
      return createAppointmentFollowUpsAppointment({
        ...options,
        restoreJobEnabled: true,
      })
    case AppointmentFollowUpsFixture.changedMeanwhile:
      return createAppointmentFollowUpsChangedMeanwhile(options)
    case AppointmentFollowUpsFixture.writeFails:
      return createAppointmentFollowUpsAppointment(options).then(
        appointment => ({
          ...appointment,
          id: APPOINTMENT_FOLLOW_UPS_MISSING_ID,
        })
      )
    default:
      return createAppointmentFollowUpsAppointment(options)
  }
}
